/**
 * 分析列表表格组件
 */
import React from 'react';
import { Table, Button, Space, Tag, Card } from 'antd';
import { BarChartOutlined, UploadOutlined } from '@ant-design/icons';
import { SKU } from '../../../types/core-types';

interface AnalysisTableProps {
    skus: SKU[];
    loading: boolean;
    onViewAnalysis: (id: number) => void;
    onUploadData: (id: number) => void;
}

export const AnalysisTable: React.FC<AnalysisTableProps> = ({ skus, loading, onViewAnalysis, onUploadData }) => {
    const columns = [
        {
            title: 'SKU名称',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (text: string) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{text}</span>,
        },
        {
            title: 'ASIN',
            dataIndex: 'asin',
            key: 'asin',
            width: 120,
            render: (text: string) => <span style={{ fontFamily: 'monospace', color: '#64748b' }}>{text}</span>,
        },
        {
            title: '品牌',
            dataIndex: 'brand',
            key: 'brand',
            width: 120,
            render: (text: string) => text || '-',
        },
        {
            title: '数据状态',
            dataIndex: 'periods_count',
            key: 'periods_count',
            width: 120,
            render: (count: number) => {
                if (count === 0) {
                    return <Tag color="default" style={{ borderRadius: 4 }}>待上传</Tag>;
                }
                if (count < 5) {
                    return <Tag color="warning" style={{ borderRadius: 4 }}>{count} 个周期</Tag>;
                }
                return <Tag color="success" style={{ borderRadius: 4 }}>{count} 个周期</Tag>;
            },
        },
        {
            title: '权重配置',
            dataIndex: 'active_weight_config_name',
            key: 'active_weight_config_name',
            width: 150,
            render: (text: string) => (
                <span style={{ color: text ? '#475569' : '#94a3b8', fontStyle: text ? 'normal' : 'italic' }}>
                    {text || '未配置'}
                </span>
            ),
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
            render: (text: string) => <span style={{ color: '#64748b' }}>{new Date(text).toLocaleString('zh-CN')}</span>,
        },
        {
            title: '操作',
            key: 'actions',
            width: 240,
            fixed: 'right' as const,
            render: (_: any, record: SKU) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<BarChartOutlined />}
                        onClick={() => onViewAnalysis(record.id)}
                        disabled={record.periods_count === 0}
                        style={{ borderRadius: 6 }}
                    >
                        查看分析
                    </Button>
                    <Button
                        size="small"
                        icon={<UploadOutlined />}
                        onClick={() => onUploadData(record.id)}
                        style={{ borderRadius: 6 }}
                    >
                        上传数据
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Card
            bordered={false}
            className="premium-card-table"
            style={{
                borderRadius: 16,
                boxShadow: '0 4px 24px rgba(0,0,0,0.02)',
                overflow: 'hidden'
            }}
            bodyStyle={{ padding: 0 }}
        >
            <Table
                columns={columns}
                dataSource={skus}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1200 }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                    style: { padding: '16px 24px' }
                }}
                rowClassName="premium-table-row"
            />
        </Card>
    );
};
